import { ReactNode } from 'react';
import { Box, Chip, InputLabel, Tooltip, Typography } from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    background: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    // Let the box shrink below its intrinsic width inside a flex parent (a
    // half-width grid card, say) instead of overflowing it.
    minWidth: 0,
  },
  id: {
    fontFamily: 'monospace',
    fontSize: '0.95rem',
    fontWeight: 600,
    letterSpacing: '0.01em',
    // A Machine ID is up to 63 characters and can be a single separator-less
    // word, which browsers will not break on their own — force it to wrap so
    // the whole identifier stays readable in a narrow container.
    minWidth: 0,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  icon: {
    flexShrink: 0,
  },
  warning: {
    marginTop: theme.spacing(1),
  },
}));

export interface MachineIdDisplayProps {
  machineId: string;
  /** Show permanence warning chip — true in create mode (FR-012) */
  showPermanenceWarning?: boolean;
  /**
   * The group level this ID belongs to ("Team", "Function", …), as named in the
   * tooltip. Defaults to the create form's case, which is always a team.
   * Callers that also render for the non-team levels (function, sub-function,
   * XP, company) — the Group entity page and the edit form — pass the entity's
   * own level label instead, since "Team" would be wrong there.
   */
  levelLabel?: string;
  /**
   * Whether the ID shown was derived from the name typed in this same view.
   * True only in the create form, which computes it live from the Team Name
   * field. Defaults to `false`, because for an existing entity the claim is not
   * safe: the backend preserves `metadata.name` across edits, and the
   * function / sub-function / XP / company records are hand-authored, so their
   * IDs need not match their current title (`demo-operations-bu` is titled
   * "DEMO - Operations"). Those callers get the neutral permanent-identifier
   * wording instead.
   */
  derivedFromName?: boolean;
  /**
   * Rendered at the end of the boxed value row, sharing its `alignItems:
   * center` — e.g. a copy button — so it centers on the ID row itself
   * rather than on the label above it.
   */
  endAdornment?: ReactNode;
}

export function MachineIdDisplay({
  machineId,
  showPermanenceWarning = false,
  levelLabel = 'Team',
  derivedFromName = false,
  endAdornment,
}: MachineIdDisplayProps) {
  const classes = useStyles();

  const tooltip = derivedFromName
    ? `The Machine ID is derived from the ${levelLabel} Name at creation and cannot be changed afterwards. It is used as the permanent identifier in all systems.`
    : `The Machine ID was fixed when this ${levelLabel} was created and cannot be changed. It is used as the permanent identifier in all systems.`;

  return (
    <Box>
      <InputLabel shrink>Machine ID</InputLabel>
      <Box className={classes.root}>
        <LockIcon fontSize="small" color="disabled" className={classes.icon} />
        <Typography className={classes.id}>{machineId || '—'}</Typography>
        <Tooltip title={tooltip} placement="right">
          <InfoOutlinedIcon
            fontSize="small"
            color="disabled"
            className={classes.icon}
          />
        </Tooltip>
        {endAdornment}
      </Box>

      {showPermanenceWarning && machineId && (
        <Chip
          className={classes.warning}
          icon={<LockIcon />}
          label="This identifier is permanent and cannot be changed after saving"
          size="small"
          color="default"
          variant="outlined"
        />
      )}
    </Box>
  );
}
